import React from "react";
import { useTranslation } from "react-i18next";
import { LogOut } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useProjectNavigation } from "../../../hooks/useProjectNavigation";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "../../auth/contexts/AuthContext";
import { SuperadminProjectSelector } from "./SuperadminProjectSelector";
import type { BrandingRole } from "./RoleBrandingHeader";

interface UserMenuProps {
  role: BrandingRole;
  testIdPrefix?: string;
}

export const UserMenu: React.FC<UserMenuProps> = ({ role, testIdPrefix = "user" }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { navigate, projectPath } = useProjectNavigation();
  const roleColor = `var(--color-role-${role})`;

  return (
    <div className="flex items-center gap-3">
      <SuperadminProjectSelector />
      <DropdownMenu>
        <DropdownMenuTrigger
          data-testid={`${testIdPrefix}-info`}
          className="flex items-center rounded-full outline-none ring-offset-2 ring-offset-background focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Avatar size="lg" style={{ ["--tw-ring-color" as any]: roleColor }} className="ring-2">
            {user?.image && <AvatarImage src={user.image} alt={user?.name || "User"} />}
            <AvatarFallback>{(user?.name || "U").slice(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={8} className="min-w-56">
          <DropdownMenuGroup>
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col gap-0.5 leading-tight">
                <span
                  data-testid={`${testIdPrefix}-name`}
                  className="truncate text-sm font-semibold text-foreground"
                >
                  {user?.name || t("logger.layout.defaultName")}
                </span>
                <span
                  data-testid={`${testIdPrefix}-role`}
                  className="truncate text-xs font-bold uppercase"
                  style={{ color: roleColor }}
                >
                  {user?.role || t("logger.layout.defaultRole")}
                </span>
              </div>
            </DropdownMenuLabel>
          </DropdownMenuGroup>
          {user?.role === "Superadmin" && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuLabel className="font-normal text-xs text-muted-foreground uppercase tracking-wider">
                  {t("admin.layout.tools", "TOOLS")}
                </DropdownMenuLabel>
                {[
                  {
                    label: t("admin.layout.logger", "Logger"),
                    icon: "bx-edit-alt",
                    path: "/logger",
                    color: "#f72d60",
                  },
                  {
                    label: t("admin.layout.editor", "Editor"),
                    icon: "bx-download",
                    path: "/editor",
                    color: "var(--color-role-editor)",
                  },
                  {
                    label: t("admin.layout.contentController", "Content Controller"),
                    icon: "bx-slider-alt",
                    path: "/controller",
                    color: "#38BDF8",
                  },
                  {
                    label: t("auth.roles.contentDirector", "Content Director"),
                    icon: "bx-crown",
                    path: "/contentdirector",
                    color: "var(--color-role-contentdirector)",
                  },
                  {
                    label: t("admin.layout.storyProducer", "Story Producer"),
                    icon: "bx-movie-play",
                    path: "/storyproducer",
                    color: "var(--color-data-story)",
                  },
                  {
                    label: t("admin.layout.showRunner", "Show Runner"),
                    icon: "bx-podcast",
                    path: "/showrunner",
                    color: "var(--color-role-showrunner)",
                  },
                  {
                    label: t("admin.layout.executive", "Executive"),
                    icon: "bx-briefcase",
                    path: "/executive",
                    color: "#EC4899",
                  },
                  {
                    label: t("auth.roles.cliente", "Cliente"),
                    icon: "bx-user-circle",
                    path: "/cliente",
                    color: "var(--color-role-cliente)",
                  },
                  {
                    label: t("auth.roles.admin", "ADMIN"),
                    icon: "bx-shield-quarter",
                    path: "/admin",
                    color: "var(--color-role-admin)",
                  },
                  {
                    label: t("auth.roles.superAdmin", "SUPERADMIN"),
                    icon: "bx-shield",
                    path: "/superadmin",
                    color: "var(--color-accent-danger)",
                  },
                ].map((mod) => (
                  <DropdownMenuItem
                    key={mod.label}
                    onClick={() => {
                      if (mod.path) navigate(projectPath(mod.path));
                    }}
                    className="cursor-pointer"
                    disabled={!mod.path}
                  >
                    <i className={`bx ${mod.icon} mr-2 text-lg`} style={{ color: mod.color }}></i>
                    <span style={{ color: mod.color, fontWeight: 600 }}>{mod.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem
              data-testid={`${testIdPrefix}-btn-logout`}
              variant="destructive"
              onClick={logout}
            >
              <LogOut />
              {t("logger.layout.logout")}
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
